import { useState, useEffect, useCallback } from 'react';

type Status = 'checking' | 'online' | 'offline' | 'degraded';

interface HealthData {
  status: string;
  timestamp: string;
  version?: string;
  db?: string;
}

interface ServerStatusBadgeProps {
  /** Show full expanded card (for overview page) or compact pill (for header) */
  variant?: 'pill' | 'card';
  /** Auto-refresh interval in ms. Default 30000 (30s). 0 = no auto-refresh */
  refreshInterval?: number;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export default function ServerStatusBadge({
  variant = 'pill',
  refreshInterval = 30000,
}: ServerStatusBadgeProps) {
  const [status, setStatus] = useState<Status>('checking');
  const [health, setHealth] = useState<HealthData | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);

  const checkHealth = useCallback(async () => {
    setChecking(true);
    const start = Date.now();
    try {
      // Hit the /health endpoint (no auth required)
      const baseUrl = API_BASE_URL.replace(/\/api$/, '');
      const res = await fetch(`${baseUrl}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });
      const ms = Date.now() - start;
      setLatencyMs(ms);
      if (res.ok) {
        const data: HealthData = await res.json();
        setHealth(data);
        setStatus(ms > 2000 ? 'degraded' : 'online');
      } else {
        setStatus('degraded');
        setHealth(null);
      }
    } catch {
      setLatencyMs(null);
      setStatus('offline');
      setHealth(null);
    } finally {
      setChecking(false);
      setLastChecked(new Date());
    }
  }, []);

  // Initial check + interval
  useEffect(() => {
    checkHealth();
    if (refreshInterval > 0) {
      const id = setInterval(checkHealth, refreshInterval);
      return () => clearInterval(id);
    }
  }, [checkHealth, refreshInterval]);

  const statusConfig = {
    checking: { label: 'Checking...', color: '#64748b', bg: '#f8fafc', dot: 'bg-gray-400', pulse: true },
    online:   { label: 'Server Online', color: '#d97706', bg: '#fffbeb', dot: 'bg-amber-500', pulse: false },
    degraded: { label: 'Degraded', color: '#d97706', bg: '#fffbeb', dot: 'bg-amber-500', pulse: true },
    offline:  { label: 'Server Offline', color: '#dc2626', bg: '#fef2f2', dot: 'bg-red-500', pulse: true },
  };

  const cfg = statusConfig[status];

  if (variant === 'pill') {
    return (
      <button
        onClick={checkHealth}
        disabled={checking}
        title={`Backend: ${cfg.label}${latencyMs ? ` (${latencyMs}ms)` : ''}. Click to recheck.`}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border"
        style={{ background: cfg.bg, color: cfg.color, borderColor: `${cfg.color}30` }}
      >
        <span className="relative flex w-2 h-2 flex-shrink-0">
          <span
            className={`${cfg.dot} rounded-full w-2 h-2 ${cfg.pulse ? 'animate-ping absolute inline-flex h-full w-full opacity-75' : ''}`}
          />
          <span className={`${cfg.dot} rounded-full w-2 h-2 relative inline-flex`} />
        </span>
        {checking ? 'Checking...' : cfg.label}
        {status === 'online' && latencyMs !== null && (
          <span className="text-xs opacity-60">{latencyMs}ms</span>
        )}
        <span className="w-3 h-3 flex items-center justify-center opacity-50">
          <i className={`ri-refresh-line text-xs ${checking ? 'animate-spin' : ''}`} />
        </span>
      </button>
    );
  }

  // Card variant — for overview page
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900 text-sm">Server Connection</h3>
        <button
          onClick={checkHealth}
          disabled={checking}
          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer transition-all"
          title="Recheck now"
        >
          <i className={`ri-refresh-line text-sm ${checking ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Status banner */}
      <div
        className="flex items-center gap-3 p-3 rounded-xl mb-4"
        style={{ background: cfg.bg }}
      >
        <span className="relative flex w-3 h-3 flex-shrink-0">
          {cfg.pulse && (
            <span className={`${cfg.dot} rounded-full absolute inline-flex h-full w-full animate-ping opacity-75`} />
          )}
          <span className={`${cfg.dot} rounded-full w-3 h-3 relative inline-flex`} />
        </span>
        <div className="flex-1">
          <p className="text-sm font-bold" style={{ color: cfg.color }}>{cfg.label}</p>
          {health?.version && (
            <p className="text-xs text-gray-500">API v{health.version}</p>
          )}
        </div>
        {status === 'online' && latencyMs !== null && (
          <span
            className="text-xs font-bold px-2 py-0.5 rounded-full"
            style={{
              background: latencyMs < 200 ? '#dcfce7' : latencyMs < 800 ? '#fef9c3' : '#fee2e2',
              color: latencyMs < 200 ? '#16a34a' : latencyMs < 800 ? '#ca8a04' : '#dc2626',
            }}
          >
            {latencyMs}ms
          </span>
        )}
      </div>

      {/* Detail rows */}
      <div className="space-y-2">
        {[
          {
            label: 'Backend API',
            value: status === 'online' ? 'Reachable' : status === 'checking' ? 'Checking...' : 'Unreachable',
            icon: 'ri-server-line',
            ok: status === 'online',
          },
          {
            label: 'Database (MariaDB)',
            value: status === 'online' ? 'Connected' : 'Unknown',
            icon: 'ri-database-2-line',
            ok: status === 'online',
          },
          {
            label: 'Response Time',
            value: latencyMs !== null ? `${latencyMs}ms` : '—',
            icon: 'ri-timer-line',
            ok: latencyMs !== null && latencyMs < 1000,
          },
          {
            label: 'Last Checked',
            value: lastChecked
              ? lastChecked.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
              : '—',
            icon: 'ri-time-line',
            ok: true,
          },
        ].map((row) => (
          <div key={row.label} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50">
            <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className={`${row.icon} text-sm text-gray-400`} />
            </span>
            <span className="text-xs text-gray-600 flex-1">{row.label}</span>
            <span
              className="text-xs font-semibold"
              style={{ color: row.ok ? '#16a34a' : '#dc2626' }}
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>

      {/* Offline help */}
      {status === 'offline' && (
        <div className="mt-3 p-3 rounded-xl border border-red-100 bg-red-50">
          <p className="text-xs font-semibold text-red-700 mb-1">Cannot reach backend server</p>
          <div className="text-xs text-red-600 mb-2">
            <span className="text-red-500 font-medium">Trying: </span>
            <code className="bg-red-100 px-1 rounded font-mono break-all">{API_BASE_URL.replace(/\/api$/, '')}/health</code>
          </div>
          <ul className="text-xs text-red-600 space-y-0.5 list-disc list-inside">
            <li>Check that the Node.js backend is running (<code className="bg-red-100 px-1 rounded">pm2 status</code>)</li>
            <li>Set <code className="bg-red-100 px-1 rounded">FRONTEND_URL={window.location.origin}</code> in backend <code className="bg-red-100 px-1 rounded">.env</code></li>
            <li>Restart backend after .env changes (<code className="bg-red-100 px-1 rounded">pm2 restart all</code>)</li>
            <li>Check nginx proxy passes <code className="bg-red-100 px-1 rounded">/api</code> to port 5000</li>
          </ul>
          <a href="/admin/database" className="mt-2 inline-flex items-center gap-1 text-xs text-red-700 font-semibold underline">
            <i className="ri-bug-line" />Run full API Diagnostics
          </a>
        </div>
      )}

      {status === 'degraded' && (
        <div className="mt-3 p-3 rounded-xl border border-amber-100 bg-amber-50">
          <p className="text-xs font-semibold text-amber-700 mb-1">Server responding slowly</p>
          <p className="text-xs text-amber-600">Response time is above 2 seconds. Check server load and database performance.</p>
        </div>
      )}
    </div>
  );
}
